import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertCircle, Paperclip } from 'lucide-react';
import { fadeInUp } from '../constants/animations';
import { buildApiUrl } from '../utils/api';
import './Careers.css';


const SALES_QUESTIONS = [
  'Why do you want to sell for Versa Launch?',
  'Describe your sales experience. What have you sold and how did you close deals?',
  'Walk us through how you would pitch Versa Launch to a local business owner.',
  'What does your availability look like and how many hours per week can you dedicate?'
];

const JOBS = [
  {
    id: 'sales-rep',
    status: 'open',
    title: 'Sales Representative',
    tags: ['Remote', 'Commission + Recurring', 'Full-Time / Part-Time'],
    goldTag: null,
    description:
      'Join the Versa Launch sales team and help local businesses grow. You\'ll pitch our all-in-one platform — websites, AI phone assistant, SEO, CRM, and more — to service business owners who need it most. We give you the leads, the tools, and the support. You bring the hustle.',
    perks: [
      'Warm leads provided — no cold list building required',
      'Commission on every deal + recurring monthly income',
      'Business phone number and CRM access from day one',
      'Flexible schedule — work from anywhere',
      'Ongoing support from our fulfillment and retention team',
      'Real upside with no earnings ceiling'
    ],
    questions: SALES_QUESTIONS
  },
  {
    id: 'software-dev',
    status: 'closed',
    title: 'Software Developer',
    tags: ['Remote', 'Full-Time'],
    goldTag: null,
    description:
      'We are not currently hiring for this role, but we are always interested in meeting talented developers. Check back soon or reach out directly.',
    perks: [],
    questions: []
  }
];

const ALLOWED_TYPES = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
const MAX_BYTES = 5 * 1024 * 1024;

function ApplicationModal({ job, onClose }) {
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [answers, setAnswers] = useState({});
  const [commissionOk, setCommissionOk] = useState('');
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [resume, setResume] = useState(null);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const overlayRef = useRef(null);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  const handleResumeChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError('Please upload a PDF or Word document.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setError('Resume must be under 5 MB.');
      return;
    }
    setError('');
    setResume(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!form.name.trim() || !form.email.trim()) {
      setError('Name and email are required.');
      return;
    }

    const unanswered = job.questions.find((q) => !answers[q]?.trim());
    if (unanswered) {
      setError('Please answer all questions before submitting.');
      return;
    }

    if (!commissionOk) {
      setError('Please answer the commission structure question.');
      return;
    }

    if (!ageConfirmed) {
      setError('You must confirm you are over the age of 18.');
      return;
    }

    setSubmitting(true);
    try {
      let resumeBase64 = null;
      let resumeFilename = null;
      let resumeMimeType = null;

      if (resume) {
        const buffer = await resume.arrayBuffer();
        resumeBase64 = btoa(String.fromCharCode(...new Uint8Array(buffer)));
        resumeFilename = resume.name;
        resumeMimeType = resume.type;
      }

      const response = await fetch(buildApiUrl('/api/career-applications'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim(),
          position: job.title,
          answers: { ...answers, 'Commission-only structure OK?': commissionOk, 'Over 18?': 'Yes' },
          resumeBase64,
          resumeFilename,
          resumeMimeType
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Submission failed.');
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="careers-modal-overlay" ref={overlayRef} onClick={handleOverlayClick}>
      <motion.div
        className="careers-modal"
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
      >
        <div className="careers-modal-head">
          <div>
            <h2>Apply — {job.title}</h2>
            <p>Versa Launch · Remote</p>
          </div>
          <button type="button" className="careers-modal-close" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        {submitted ? (
          <div className="careers-modal-success">
            <div className="careers-modal-success-icon">🎉</div>
            <h3>Application Submitted</h3>
            <p>
              Thanks for applying! We've sent a confirmation to <strong>{form.email}</strong>. Our team reviews
              applications within 1–3 business days and will reach out if it's a fit.
            </p>
          </div>
        ) : (
          <form className="careers-modal-body" onSubmit={handleSubmit} noValidate>
            <p className="careers-form-section-label">Your Info</p>
            <div className="careers-form-row">
              <div className="careers-form-field">
                <label htmlFor="app-name">Full Name *</label>
                <input
                  id="app-name"
                  type="text"
                  placeholder="Jane Smith"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>
              <div className="careers-form-field">
                <label htmlFor="app-email">Email *</label>
                <input
                  id="app-email"
                  type="email"
                  placeholder="jane@email.com"
                  value={form.email}
                  onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  required
                />
              </div>
            </div>
            <div className="careers-form-field">
              <label htmlFor="app-phone">Phone (optional)</label>
              <input
                id="app-phone"
                type="tel"
                placeholder="(555) 000-0000"
                value={form.phone}
                onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
              />
            </div>

            <p className="careers-form-section-label">Application Questions</p>
            {job.questions.map((q, i) => (
              <div key={i} className="careers-form-field">
                <label htmlFor={`app-q-${i}`}>{q} *</label>
                <textarea
                  id={`app-q-${i}`}
                  rows={3}
                  placeholder="Your answer..."
                  value={answers[q] || ''}
                  onChange={(e) => setAnswers((p) => ({ ...p, [q]: e.target.value }))}
                  required
                />
              </div>
            ))}

            <div className="careers-form-field">
              <label htmlFor="app-commission">Are you comfortable with a commission-only + recurring pay structure? *</label>
              <select
                id="app-commission"
                value={commissionOk}
                onChange={(e) => setCommissionOk(e.target.value)}
                style={{ padding: '11px 14px', border: '1.5px solid #e5e7eb', borderRadius: '10px', fontSize: '0.9rem', color: commissionOk ? '#0b0b0b' : '#9ca3af', background: '#ffffff', outline: 'none', fontFamily: 'inherit' }}
                required
              >
                <option value="">Select...</option>
                <option value="Yes">Yes</option>
                <option value="No">No</option>
              </select>
            </div>

            <label className="careers-age-checkbox">
              <input
                type="checkbox"
                checked={ageConfirmed}
                onChange={(e) => setAgeConfirmed(e.target.checked)}
              />
              <span>I confirm I am over the age of 18 *</span>
            </label>

            <p className="careers-form-section-label">Resume</p>
            <div className="careers-resume-upload">
              <input type="file" accept=".pdf,.doc,.docx" onChange={handleResumeChange} />
              <div className="careers-resume-upload-icon"><Paperclip size={22} strokeWidth={1.8} /></div>
              {resume ? (
                <p><span className="careers-resume-upload-name">✓ {resume.name}</span></p>
              ) : (
                <p><strong>Click to upload</strong> or drag & drop<br />PDF or Word · Max 5 MB</p>
              )}
            </div>

            {error && (
              <div className="careers-form-error">
                <AlertCircle size={16} /> {error}
              </div>
            )}

            <button type="submit" className="careers-submit-btn" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit Application'}
            </button>
            <p className="careers-submit-disclaimer">
              By submitting you agree that Versa Launch may contact you about this application via email or phone.
            </p>
          </form>
        )}
      </motion.div>
    </div>
  );
}

function JobCard({ job, onApply }) {
  const isOpen = job.status === 'open';
  return (
    <motion.div
      className={`careers-job-card${isOpen ? '' : ' careers-job-card--closed'}`}
      variants={fadeInUp}
    >
      <div className="careers-job-card-header">
        <h3>{job.title}</h3>
        <span className={`careers-job-pill careers-job-pill--${job.status}`}>
          <span className="careers-job-pill-dot" />
          {isOpen ? 'Open' : 'Closed'}
        </span>
      </div>

      <div className="careers-job-meta">
        {job.goldTag && <span className="careers-job-tag careers-job-tag--gold">{job.goldTag}</span>}
        {job.tags.map((t) => (
          <span key={t} className="careers-job-tag">{t}</span>
        ))}
      </div>

      <p className="careers-job-desc">{job.description}</p>

      {job.perks.length > 0 && (
        <ul className="careers-job-perks">
          {job.perks.map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}

      <button
        type="button"
        className={`careers-job-cta careers-job-cta--${job.status}`}
        onClick={() => isOpen && onApply(job)}
        disabled={!isOpen}
      >
        {isOpen ? 'Apply Now' : 'Applications Closed'}
      </button>
    </motion.div>
  );
}

function Careers() {
  const [activeJob, setActiveJob] = useState(null);

  return (
    <main className="careers-page">
      {/* Hero */}
      <motion.section
        className="careers-hero"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.25 }}
      >
        <div className="container">
          <motion.span className="careers-kicker" variants={fadeInUp} custom={0}>Join the Team</motion.span>
          <motion.h1 variants={fadeInUp} custom={1}>
            Help businesses <span className="careers-gold-text">grow</span>.<br />
            Build your income doing it.
          </motion.h1>
          <motion.p variants={fadeInUp} custom={2}>
            Versa Launch is building the go-to growth platform for local service businesses. We're
            looking for driven closers who want real upside — not a salary ceiling.
          </motion.p>
        </div>
      </motion.section>

      {/* Open Positions */}
      <motion.section
        className="careers-jobs"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className="container">
          <motion.div className="careers-jobs-grid" variants={fadeInUp} custom={0}>
            {JOBS.map((job) => (
              <JobCard key={job.id} job={job} onApply={setActiveJob} />
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Application Modal */}
      <AnimatePresence>
        {activeJob && (
          <ApplicationModal job={activeJob} onClose={() => setActiveJob(null)} />
        )}
      </AnimatePresence>
    </main>
  );
}

export default Careers;
